import type { GameState, TerrainOnlyTileKind } from "@dailywars/core/engine";

import { preloadBadgeSprites } from "./badgeSprites";
import { preloadBuildingIcons } from "./buildingSprites";
import { preloadExplosionSheets } from "./fxSprites";
import { isImageReady } from "./imageReady";
import { preloadRoadAutotileImages } from "./roadSprites";
import { terrainKindsForMapTile } from "./roadTileIcon";
import { preloadSeaShoreImages } from "./seaShoreSprites";
import { getTerrainImage } from "./svgAssets";
import {
  getSpriteTerrainImage,
  usesSpriteTerrainFullTile,
  usesSpriteTerrainOverlay,
} from "./terrainSprites";
import { preloadUnitSprites } from "./unitSprites";

function awaitImage(img: HTMLImageElement): Promise<void> {
  if (isImageReady(img)) return Promise.resolve();
  if (typeof img.decode === "function") {
    return img.decode().catch(() => undefined);
  }
  return new Promise<void>((resolve) => {
    const done = () => resolve();
    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  });
}

function pushTerrainKindImage({
  images,
  terrainKind,
  state,
  x,
  y,
}: {
  images: HTMLImageElement[];
  terrainKind: TerrainOnlyTileKind;
  state: GameState;
  x: number;
  y: number;
}): void {
  if (usesSpriteTerrainFullTile({ kind: terrainKind })) {
    if (terrainKind !== "sea") {
      images.push(getSpriteTerrainImage({ theme: state.theme, kind: terrainKind }));
    }
    return;
  }
  if (usesSpriteTerrainOverlay({ theme: state.theme, kind: terrainKind })) {
    images.push(getTerrainImage({ kind: "plain", x, y, theme: state.theme }));
    images.push(getSpriteTerrainImage({ theme: state.theme, kind: terrainKind }));
    return;
  }
  images.push(getTerrainImage({ kind: terrainKind, x, y, theme: state.theme }));
}

function collectSpriteTerrainImages({ state }: { state: GameState }): HTMLImageElement[] {
  const images: HTMLImageElement[] = [];

  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const tile = state.tiles[y * state.width + x]!;
      for (const terrainKind of terrainKindsForMapTile({
        kind: tile.kind,
        tiles: state.tiles,
        width: state.width,
        x,
        y,
      })) {
        pushTerrainKindImage({ images, terrainKind, state, x, y });
      }
    }
  }

  return images;
}

function collectTerrainImages(state: GameState): HTMLImageElement[] {
  const images = collectSpriteTerrainImages({ state });
  let hasRoads = false;
  let hasSea = false;

  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const tile = state.tiles[y * state.width + x]!;
      if (tile.kind === "road") hasRoads = true;
      if (tile.kind === "sea") hasSea = true;
    }
  }

  if (hasRoads) {
    images.push(...preloadRoadAutotileImages({ theme: state.theme }));
  }

  if (hasSea) {
    images.push(
      ...preloadSeaShoreImages({
        tiles: state.tiles,
        width: state.width,
        height: state.height,
        theme: state.theme,
      }),
    );
  }

  return images;
}

export async function preloadBoardImages(state: GameState): Promise<void> {
  const images: HTMLImageElement[] = [
    ...preloadUnitSprites(),
    ...preloadBuildingIcons(),
    ...preloadBadgeSprites(),
    ...preloadExplosionSheets(),
    ...collectTerrainImages(state),
  ];

  await Promise.all(images.map(awaitImage));
}
